import { useMemo } from "react";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { TOpenModalDepartamento } from "..";
import { TDepartamentosEstoque } from "../../../../../../types/api/estoque/departamentos-estoque";

export default function DialogCriacaoEditDepartamentos({
  open,
  handleClose,
  defaultValue,
}: DialogDepartamentosProps) {

  const queryClient = useQueryClient();

  const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue])
  const valoresIniciais = useMemo(() => defaultValue?.defaultValue ? defaultValue.defaultValue : null, [defaultValue])

  const { control, handleSubmit } = useForm<TDepartamentosEstoque>({
    defaultValues: valoresIniciais ? valoresIniciais : defaultValues,
  });

  //Querys e mutations
  const { mutate: criarDepartamento, isLoading: isLoadingRegister } =
    useMutation({
      mutationKey: "criar-departamento",
      mutationFn: (inputs: TDepartamentosEstoque) =>
        axiosClient.post("estoque/configuracoes/criar-departamento", inputs),
      onError: (error) => {
        console.error(error);
        enqueueSnackbar("Erro ao cadastrar departamento!", {
          variant: "error",
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["pesquisar-departamentos"]);
        handleClose();
        enqueueSnackbar("Departamento cadastrado com sucesso!", {
          variant: "success",
        });
      },
    });

  const { mutate: editarDepartamento, isLoading: isLoadingUpdate } =
    useMutation({
      mutationKey: "editar-departamento",
      mutationFn: (inputs: TDepartamentosEstoque) =>
        axiosClient.put(
          `estoque/configuracoes/atualizar-departamento/${valoresIniciais?.id}`,
          inputs
        ),
      onError: (error) => {
        console.error(error);
        enqueueSnackbar("Erro ao atualizar departamento!", {
          variant: "error",
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["pesquisar-departamentos"]);
        handleClose()
        enqueueSnackbar("Departamento atualizado com sucesso!", {
          variant: "success",
        });
      },
    });
  //

  const onSubmit = (data: TDepartamentosEstoque) => {
    if (isEdit) editarDepartamento(data);
    if (!isEdit) criarDepartamento(data);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEdit ? "Editar" : "Cadastrar"} Departamento
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box px={0.5} my={1}>
              <Grid container spacing={2}>
                <Grid size={{xs: 12, md: 6}}>
                  <Controller
                    name="nome"
                    control={control}
                    rules={{ required: "Nome é obrigatório" }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Nome"
                        defaultValue={valoresIniciais?.nome}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        fullWidth
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
                  <Controller
                    name="id"
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label="Código"
                        type="number"
                        fullWidth
                        defaultValue={valoresIniciais?.id}
                        value={field.value}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleClose}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              type="submit"
              disabled={isLoadingRegister || isLoadingUpdate}
            >
              {(isLoadingRegister || isLoadingUpdate) && (
                <CircularProgress size={20} sx={{ marginRight: 1 }} />
              )}
              {isEdit ? "Atualizar" : "Cadastrar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

interface DialogDepartamentosProps {
  open: boolean;
  handleClose: () => void;
  defaultValue: TOpenModalDepartamento
}

const defaultValues: TDepartamentosEstoque = {
  id: 0,
  nome: "",
};
